/* @jsxImportSource @emotion/react */
/* eslint-disable formatjs/no-literal-string-in-jsx */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { css } from '@emotion/react'
import ReactMarkdown from 'react-markdown'

import { Button, PopoverOnHover, SuperSourceIcon } from '@slite/components'

import { AgentRow } from '../../../../components/AgentRow'
import {
  type AiSearchScenario,
  type Citation,
  STEP_STAGGER_MS,
  STREAM_CHARS_PER_TICK,
  STREAM_TICK_MS,
} from './content'

// ─── Question bubble — matches @slite/screens SuperQuestionLayout ───────

const QuestionBubble = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      position: 'sticky',
      right: 0,
      top: 0,
      zIndex: 1,
      marginBottom: -40,
    }}
  >
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        background: 'var(--shade-100)',
        paddingTop: 0,
      }}
    >
      <div
        className="radius-xlarger background-shade-60 padding-horizontal-16 padding-vertical-12 shade-00"
        style={{ maxWidth: '82%' }}
      >
        {children}
      </div>
    </div>
    <div
      style={{
        width: '100%',
        height: 40,
        background:
          'linear-gradient(in oklab 180deg, var(--shade-100) 0%, transparent 100%)',
      }}
    />
  </div>
)

// ─── Citation token — inline underline + hover popover (SourceCard) ─────
//
// Mirrors @slite/screens TextWithSourceReference: gradient underlay that
// slides up from below the text on hover; dotted bottom border that darkens.

const citationTokenCss = css`
  /* Parent (AppInterface) disables pointer events so the demo surface is
     inert. Re-enable here so the citation hover card stays interactive — the
     one bit of the demo that's meant to be explored. */
  pointer-events: auto;
  cursor: pointer;
  padding: 0 2px;
  border-radius: 4px 4px 0 0;
  border-bottom: 2px dotted var(--shade-40);
  background-image: linear-gradient(
    to top,
    var(--alpha-purple-45) 0%,
    var(--alpha-purple-45) 100%
  );
  background-size: 100% 100%;
  background-position-y: 100%;
  background-repeat: no-repeat;
  transition: all 250ms ease-in-out;
  &:hover {
    background-position-y: 0;
    border-bottom-color: var(--purple-45);
  }
`

const CitationCard = ({ citation }: { citation: Citation }) => (
  <div
    className="flexbox flexbox-column background-shade-100 radius-large border-small border-shade-45 padding-horizontal-12 padding-vertical-8 shadow-medium"
    style={{ minWidth: 240, maxWidth: 320, gap: 6 }}
  >
    <div className="flexbox flexbox-align-center" style={{ gap: 8 }}>
      <SuperSourceIcon sourceType={citation.source} size={16} />
      <span className="font-weight-semibold shade-00 ellipsis">
        {citation.title}
      </span>
    </div>
    <div className="shade-20" style={{ fontSize: 13, lineHeight: '1.4em' }}>
      {citation.snippet}
    </div>
  </div>
)

const CitationToken = ({
  citation,
  children,
}: {
  citation: Citation
  children: React.ReactNode
}) => (
  <PopoverOnHover
    positions={['top', 'bottom']}
    openDelay={120}
    allowContentHover
    target={({ props, ref }) => (
      <span
        {...props}
        ref={ref as React.RefCallback<HTMLSpanElement>}
        css={citationTokenCss}
      >
        {children}
      </span>
    )}
    content={({ ref }) => (
      <div
        ref={ref as React.RefCallback<HTMLDivElement>}
        style={{ pointerEvents: 'auto' }}
      >
        <CitationCard citation={citation} />
      </div>
    )}
  />
)

// ─── "Based on N sources" footer button — matches SuperMessage ──────────

const SourcesButton = ({ scenario }: { scenario: AiSearchScenario }) => {
  const uniqueSources = Array.from(
    new Set(scenario.sources.map((s) => s.source))
  )
  return (
    <Button variant="secondary" size="small">
      <span>Based on {scenario.sources.length} sources</span>
      <span
        className="flexbox flexbox-align-center"
        style={{ gap: 4, marginLeft: 8 }}
      >
        {uniqueSources.map((source) => (
          <SuperSourceIcon key={source} sourceType={source} size={12} />
        ))}
      </span>
    </Button>
  )
}

// ─── Markdown components (matches BASE_MARKDOWN_COMPONENTS) ─────────────

const buildMarkdownComponents = (scenario: AiSearchScenario) => ({
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="ask-message-heading shade-00 font-weight-bold">
      {children}
    </h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="ask-message-heading shade-00 font-weight-bold">
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="ask-message-heading shade-00 font-weight-bold">
      {children}
    </h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="shade-10">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <span className="font-weight-semibold shade-00">{children}</span>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    if (href?.startsWith('cite:')) {
      const id = href.slice('cite:'.length)
      const citation = scenario.citations[id]
      if (citation)
        return <CitationToken citation={citation}>{children}</CitationToken>
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="blue-15 underline font-weight-semibold"
      >
        {children}
      </a>
    )
  },
})

// ─── Animation timing knobs ─────────────────────────────────────────────

const STEP_PULSE_MS = 2000 // each step pulses for 2s

// ─── Animation state ────────────────────────────────────────────────────
//
// Activates when `isActive` flips true. Resets every state and clears every
// timer when `isActive` flips false OR when `runKey` changes — so scrolling
// away/back AND clicking a scenario button both replay the demo from t=0.

const useSearchConversationAnimation = (
  scenario: AiSearchScenario,
  isActive: boolean,
  runKey: number,
  scrollRef: React.RefObject<HTMLDivElement | null>
) => {
  const [visibleStepCount, setVisibleStepCount] = useState(0)
  const [streamedAnswer, setStreamedAnswer] = useState('')
  const timersRef = useRef<{
    timeouts: ReturnType<typeof setTimeout>[]
    intervals: ReturnType<typeof setInterval>[]
  }>({ timeouts: [], intervals: [] })

  useEffect(() => {
    // Reference runKey so the linter sees it as a real dep — bumping runKey
    // is how the parent forces a replay when the scenario changes (or just
    // restarts) while `isActive` stays true.
    void runKey
    const clearAll = () => {
      timersRef.current.timeouts.forEach(clearTimeout)
      timersRef.current.intervals.forEach(clearInterval)
      timersRef.current.timeouts = []
      timersRef.current.intervals = []
    }

    if (!isActive) {
      clearAll()
      setVisibleStepCount(0)
      setStreamedAnswer('')
      return undefined
    }

    setVisibleStepCount(0)
    setStreamedAnswer('')

    for (let i = 0; i < scenario.steps.length; i++) {
      const t = setTimeout(() => {
        setVisibleStepCount(i + 1)
      }, i * STEP_STAGGER_MS)
      timersRef.current.timeouts.push(t)
    }

    const streamStart = setTimeout(() => {
      let pos = 0
      const iv = setInterval(() => {
        pos = Math.min(pos + STREAM_CHARS_PER_TICK, scenario.answer.length)
        setStreamedAnswer(scenario.answer.slice(0, pos))
        if (pos >= scenario.answer.length) clearInterval(iv)
      }, STREAM_TICK_MS)
      timersRef.current.intervals.push(iv)
    }, scenario.steps.length * STEP_STAGGER_MS)
    timersRef.current.timeouts.push(streamStart)

    return clearAll
  }, [isActive, scenario, runKey])

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  })

  return {
    visibleStepCount,
    streamedAnswer,
    streamingStarted: visibleStepCount === scenario.steps.length,
    streamingDone: streamedAnswer.length === scenario.answer.length,
  }
}

// ─── SearchConversation ─────────────────────────────────────────────────

export const SearchConversation = ({
  scenario,
  isActive,
  runKey,
  scrollRef,
}: {
  scenario: AiSearchScenario
  isActive: boolean
  // Bump to force a replay even when isActive is already true (e.g. on
  // scenario change from a button click).
  runKey: number
  scrollRef: React.RefObject<HTMLDivElement | null>
}) => {
  const { visibleStepCount, streamedAnswer, streamingStarted, streamingDone } =
    useSearchConversationAnimation(scenario, isActive, runKey, scrollRef)

  const markdownComponents = buildMarkdownComponents(scenario)

  return (
    <>
      <QuestionBubble>{scenario.question}</QuestionBubble>
      <div className="flexbox flexbox-column flexbox-gap-12 margin-top-12">
        {scenario.steps.slice(0, visibleStepCount).map((step) => (
          <AgentRow
            key={step.label}
            icon={
              <SuperSourceIcon
                sourceType={step.source}
                size={14}
                isSourceTypeIcon
              />
            }
            title={step.label}
            pulse
            durationMs={STEP_PULSE_MS}
          />
        ))}
      </div>

      {streamingStarted && (
        <>
          <div
            style={{
              height: 1,
              background: 'var(--alpha-shade-45)',
              width: '100%',
            }}
          />

          <div className="simple-rich-text break-word">
            <ReactMarkdown
              components={markdownComponents}
              urlTransform={(url) => url}
            >
              {streamedAnswer}
            </ReactMarkdown>
          </div>
        </>
      )}

      {streamingDone && (
        <div>
          <SourcesButton scenario={scenario} />
        </div>
      )}
    </>
  )
}
